/**
 * Build a flattened PNG blob from CanvasEditor layer state (WYSIWYG export).
 */

import { hexToRgba } from "./dropShadowFilter";
import { drawWatermarkOnCtx } from "./drawWatermarkOnCanvas";

const MAX_EXPORT_EDGE = 2000;

export function parseExportPixelSize(resolutionLabel, canvasSize, exportSize = "standard") {
  let width = 0;
  let height = 0;

  if (resolutionLabel) {
    let pixelMatch = resolutionLabel.match(/(\d+)\s*×\s*(\d+)/);
    if (!pixelMatch) {
      pixelMatch = resolutionLabel.match(/(\d+)\s*x\s*(\d+)/i);
    }
    if (!pixelMatch) {
      pixelMatch = resolutionLabel.match(/(\d+)\s*[*×xX]\s*(\d+)/);
    }
    if (pixelMatch) {
      width = parseInt(pixelMatch[1], 10);
      height = parseInt(pixelMatch[2], 10);
    }
  }

  if (!width || !height) {
    const cw = Math.max(1, Math.round(canvasSize?.width || 1));
    const ch = Math.max(1, Math.round(canvasSize?.height || 1));
    const aspect = cw / ch;
    if (aspect >= 1) {
      width = MAX_EXPORT_EDGE;
      height = Math.round(MAX_EXPORT_EDGE / aspect);
    } else {
      height = MAX_EXPORT_EDGE;
      width = Math.round(MAX_EXPORT_EDGE * aspect);
    }
  }

  if (exportSize === "standard") {
    const longEdge = Math.max(width, height);
    if (longEdge > MAX_EXPORT_EDGE) {
      const scale = MAX_EXPORT_EDGE / longEdge;
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
  }

  return {
    width: Math.max(1, width),
    height: Math.max(1, height),
  };
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error("Missing image URL"));
      return;
    }
    const img = new Image();
    if (url.startsWith("http://") || url.startsWith("https://")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

function drawCover(ctx, img, cw, ch) {
  const ir = img.naturalWidth / img.naturalHeight;
  const cr = cw / ch;
  let dw;
  let dh;
  let dx;
  let dy;
  if (ir > cr) {
    dh = ch;
    dw = dh * ir;
    dx = (cw - dw) / 2;
    dy = 0;
  } else {
    dw = cw;
    dh = dw / ir;
    dx = 0;
    dy = (ch - dh) / 2;
  }
  ctx.drawImage(img, dx, dy, dw, dh);
}

/**
 * Layout of the subject bitmap relative to the object box (0,0)-(w,h).
 * softOverflow matches the editor CSS: full PNG aligned so the crop fills the box
 * (AI shadows and the unclipped .subject-shadow layer).
 */
function measureSubjectDraw(img, w, h, cropState, softOverflow) {
  const srcX = cropState?.x ?? 0;
  const srcY = cropState?.y ?? 0;
  const srcW =
    cropState?.width > 0 ? cropState.width : img.naturalWidth;
  const srcH =
    cropState?.height > 0 ? cropState.height : img.naturalHeight;

  if (softOverflow && srcW > 0 && srcH > 0) {
    const dispW = w * (img.naturalWidth / srcW);
    const dispH = h * (img.naturalHeight / srcH);
    const destX = -srcX * (w / srcW);
    const destY = -srcY * (h / srcH);
    return {
      srcX: 0,
      srcY: 0,
      srcW: img.naturalWidth,
      srcH: img.naturalHeight,
      destX,
      destY,
      destW: dispW,
      destH: dispH,
    };
  }

  return {
    srcX,
    srcY,
    srcW,
    srcH,
    destX: 0,
    destY: 0,
    destW: w,
    destH: h,
  };
}

function rasterizeSubject(img, w, h, cropState, softOverflow) {
  const m = measureSubjectDraw(img, w, h, cropState, softOverflow);
  const minX = Math.min(0, m.destX);
  const minY = Math.min(0, m.destY);
  const maxX = Math.max(w, m.destX + m.destW);
  const maxY = Math.max(h, m.destY + m.destH);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.ceil(maxX - minX));
  canvas.height = Math.max(1, Math.ceil(maxY - minY));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    img,
    m.srcX,
    m.srcY,
    m.srcW,
    m.srcH,
    m.destX - minX,
    m.destY - minY,
    m.destW,
    m.destH
  );
  return { canvas, minX, minY };
}

function drawRasterOnCanvas(ctx, raster, opts, shadow = null, filter = null) {
  const {
    objectPosition,
    objectSize,
    objectRotation,
    objectFlip,
    scale,
  } = opts;
  const x = objectPosition.x * scale;
  const y = objectPosition.y * scale;
  const w = objectSize.width * scale;
  const h = objectSize.height * scale;

  ctx.save();
  ctx.translate(x + w / 2, y + h / 2);
  ctx.rotate((objectRotation * Math.PI) / 180);
  ctx.scale(objectFlip?.x ?? 1, objectFlip?.y ?? 1);
  if (filter) ctx.filter = filter;
  if (shadow) {
    // Draw a pre-rasterized canvas with ctx.shadow* — CSS filter: drop-shadow()
    // on drawImage(HTMLImageElement) is clipped / ignored in Chromium.
    ctx.shadowColor = hexToRgba(shadow.color, shadow.opacity ?? 1);
    ctx.shadowBlur = Math.max(0, Number(shadow.blur) || 0) * scale;
    ctx.shadowOffsetX = (Number(shadow.offsetX ?? shadow.x) || 0) * scale;
    ctx.shadowOffsetY = (Number(shadow.offsetY ?? shadow.y) || 0) * scale;
  }
  ctx.drawImage(raster.canvas, -w / 2 + raster.minX, -h / 2 + raster.minY);
  ctx.restore();
}

function drawSubjectLayer(ctx, img, opts) {
  const {
    objectSize,
    cropState,
    scale,
    filter,
    softOverflow = false,
    shadow = null,
  } = opts;
  const w = objectSize.width * scale;
  const h = objectSize.height * scale;
  const raster = rasterizeSubject(img, w, h, cropState, softOverflow);
  drawRasterOnCanvas(ctx, raster, opts, shadow, filter);
}

function drawOverlayImage(ctx, img, layer, scale) {
  const x = (layer.x || 0) * scale;
  const y = (layer.y || 0) * scale;
  const w = Math.max(1, (layer.width || 1) * scale);
  const h = Math.max(1, (layer.height || 1) * scale);
  const rotation = Number(layer.rotation) || 0;
  const flipX = layer.flipH ? -1 : 1;
  const flipY = layer.flipV ? -1 : 1;
  const crop = layer.contentCrop;

  ctx.save();
  ctx.translate(x + w / 2, y + h / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.scale(flipX, flipY);
  if (
    crop &&
    crop.width > 0 &&
    crop.height > 0 &&
    crop.imageWidth > 0 &&
    crop.imageHeight > 0
  ) {
    ctx.drawImage(
      img,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      -w / 2,
      -h / 2,
      w,
      h
    );
  } else {
    ctx.drawImage(img, -w / 2, -h / 2, w, h);
  }
  ctx.restore();
}

/**
 * @param {object} state — snapshot from CanvasEditor
 * @returns {Promise<Blob>}
 */
export async function compositeEditorToPngBlob(state) {
  const {
    imageUrl,
    backgroundColor = "#ffffff",
    backgroundImageUrl,
    canvasSize,
    objectSize,
    objectPosition,
    objectRotation,
    objectFlip,
    cropState,
    shadowState,
    outlineState,
    outlineImageUrl,
    outlineBleedPx = 0,
    resolutionLabel,
    exportSize,
    // Default on: baked AI shadows sit outside the opaque subject crop.
    softOverflow = true,
    objectLayerVisible = true,
    layers = null,
    watermarkState = null,
  } = state;

  if (!canvasSize?.width || !canvasSize?.height) {
    throw new Error("Editor is not ready for export.");
  }

  const { width: exportW, height: exportH } = parseExportPixelSize(
    resolutionLabel,
    canvasSize,
    exportSize
  );
  const scale = exportW / canvasSize.width;

  const layerList = Array.isArray(layers) ? layers : [];
  const imageLayerList = layerList.filter(
    (l) => l && l.type === "image" && l.visible !== false && l.imageUrl
  );

  const [subjectImg, bgImg, outlineImg, ...overlayImgs] = await Promise.all([
    imageUrl && objectLayerVisible ? loadImage(imageUrl) : Promise.resolve(null),
    backgroundImageUrl ? loadImage(backgroundImageUrl) : Promise.resolve(null),
    imageUrl &&
      objectLayerVisible &&
      outlineState?.enabled &&
      outlineImageUrl
      ? loadImage(outlineImageUrl)
      : Promise.resolve(null),
    ...imageLayerList.map((l) => loadImage(l.imageUrl).catch(() => null)),
  ]);

  const overlayImgById = new Map();
  imageLayerList.forEach((l, i) => {
    overlayImgById.set(l.id, overlayImgs[i]);
  });

  const canvas = document.createElement("canvas");
  canvas.width = exportW;
  canvas.height = exportH;
  const ctx = canvas.getContext("2d");

  const isTransparent =
    !backgroundColor ||
    backgroundColor === "transparent" ||
    backgroundColor === "rgba(0,0,0,0)";

  if (!isTransparent) {
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, exportW, exportH);
  } else {
    ctx.clearRect(0, 0, exportW, exportH);
  }

  if (bgImg) {
    drawCover(ctx, bgImg, exportW, exportH);
  }

  const drawObjectStack = () => {
    if (!subjectImg) return;
    const w = objectSize.width * scale;
    const h = objectSize.height * scale;
    const subjectOpts = {
      objectPosition,
      objectSize,
      objectRotation,
      objectFlip,
      cropState,
      scale,
      // Full bitmap aligned to the crop box so baked shadows are not clipped.
      softOverflow: true,
    };

    if (outlineImg && outlineState?.enabled && outlineState.width > 0) {
      const bleed = Math.max(0, Number(outlineBleedPx) || 0);
      drawSubjectLayer(ctx, outlineImg, {
        ...subjectOpts,
        cropState: {
          x: (cropState?.x ?? 0) + bleed,
          y: (cropState?.y ?? 0) + bleed,
          width: cropState?.width > 0 ? cropState.width : outlineImg.naturalWidth,
          height:
            cropState?.height > 0 ? cropState.height : outlineImg.naturalHeight,
        },
        filter: outlineState.blur
          ? `blur(${outlineState.blur * scale}px)`
          : "none",
      });
    }

    if (shadowState?.enabled) {
      // Match .subject-shadow: unclipped full bitmap + canvas shadow (not CSS filter).
      const shadowRaster = rasterizeSubject(subjectImg, w, h, cropState, true);
      drawRasterOnCanvas(ctx, shadowRaster, subjectOpts, shadowState);
    }

    drawSubjectLayer(ctx, subjectImg, subjectOpts);
  };

  const movable = layerList
    .filter((l) => l && l.id !== "background" && l.visible !== false)
    .sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));

  if (movable.length === 0) {
    // Fallback: no layer metadata — draw object only
    drawObjectStack();
  } else {
    for (const layer of movable) {
      if (layer.type === "object" || layer.id === "object") {
        drawObjectStack();
      } else if (layer.type === "image") {
        const img = overlayImgById.get(layer.id);
        if (img) drawOverlayImage(ctx, img, layer, scale);
      }
    }
  }

  if (watermarkState?.enabled && watermarkState?.embed) {
    await drawWatermarkOnCtx(ctx, {
      watermark: watermarkState,
      stageWidth: exportW,
      stageHeight: exportH,
    });
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to encode export image."));
      },
      "image/png"
    );
  });
}
